import React from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem 2rem 0.5rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 3px solid var(--primary-color);
`;

const Section = styled.section`
  margin-bottom: 1.75rem;
  padding: 0; /* Override global section padding */
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  color: #444;
  margin-bottom: 0.75rem;
  font-weight: 600;
`;

const Content = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: #555;
  
  p {
    margin-bottom: 0.5rem;
  }
  
  ul, ol {
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.25rem;
    }
  }
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LastUpdated = styled.p`
  font-style: italic;
  color: #777;
  font-size: 0.9rem;
`;

const TermsPage: React.FC = () => {
  return (
    <PageContainer className="font--jakarta">
      <SEO 
        title="Terms of Service"
        description="Terms of Service for UPTREND Systems. Please review these terms carefully before using our website or software development services."
        keywords="terms of service, UPTREND Systems terms, software development terms, legal agreement, service terms, Ontario software company terms"
        canonicalUrl="/terms"
        noIndex={false}
        pageType="legal"
        location="Whitby"
      />
      <PageTitle>Terms of Service</PageTitle>
      
      <Section>
        <Content>
          <p>Welcome to Uptrend Systems. Please read these Terms of Service carefully before using our website or services.</p>
          <p>By accessing or using our website at <a href="https://uptrendsystems.com">uptrendsystems.com</a> (the "Service"), you agree to be bound by these Terms. If you disagree with any part of the terms, you may not access the Service.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>1. Terms</SectionTitle>
        <Content>
          <p>By accessing the website at <a href="https://uptrendsystems.com">uptrendsystems.com</a>, you are agreeing to be bound by these terms of service, all applicable laws and regulations, and agree that you are responsible for compliance with any applicable local laws. If you do not agree with any of these terms, you are prohibited from using or accessing this site. The materials contained in this website are protected by applicable copyright and trademark law.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>2. Use License</SectionTitle>
        <Content>
          <p>Permission is granted to temporarily download one copy of the materials (information or software) on Uptrend Systems's website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:</p>
          <ul>
            <li>modify or copy the materials;</li>
            <li>use the materials for any commercial purpose, or for any public display (commercial or non-commercial);</li>
            <li>attempt to decompile or reverse engineer any software contained on Uptrend Systems's website;</li>
            <li>remove any copyright or other proprietary notations from the materials; or</li>
            <li>transfer the materials to another person or "mirror" the materials on any other server.</li>
          </ul>
          <p>This license shall automatically terminate if you violate any of these restrictions and may be terminated by Uptrend Systems at any time. Upon terminating your viewing of these materials or upon the termination of this license, you must destroy any downloaded materials in your possession whether in electronic or printed format.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>3. Disclaimer</SectionTitle>
        <Content>
          <p>The materials on Uptrend Systems's website are provided on an 'as is' basis. Uptrend Systems makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.</p>
          <p>Further, Uptrend Systems does not warrant or make any representations concerning the accuracy, likely results, or reliability of the use of the materials on its website or otherwise relating to such materials or on any sites linked to this site.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>4. Limitations</SectionTitle>
        <Content>
          <p>In no event shall Uptrend Systems or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Uptrend Systems's website, even if Uptrend Systems or a Uptrend Systems authorized representative has been notified orally or in writing of the possibility of such damage. Because some jurisdictions do not allow limitations on implied warranties, or limitations of liability for consequential or incidental damages, these limitations may not apply to you.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>5. Accuracy of Materials</SectionTitle>
        <Content>
          <p>The materials appearing on Uptrend Systems's website could include technical, typographical, or photographic errors. Uptrend Systems does not warrant that any of the materials on its website are accurate, complete or current. Uptrend Systems may make changes to the materials contained on its website at any time without notice. However Uptrend Systems does not make any commitment to update the materials.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>6. Links</SectionTitle>
        <Content>
          <p>Uptrend Systems has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by Uptrend Systems of the site. Use of any such linked website is at the user's own risk.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>7. Modifications</SectionTitle>
        <Content>
          <p>Uptrend Systems may revise these terms of service for its website at any time without notice. By using this website you are agreeing to be bound by the then current version of these terms of service.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>8. Governing Law</SectionTitle>
        <Content>
          <p>These terms and conditions are governed by and construed in accordance with the laws of Ontario, Canada and you irrevocably submit to the exclusive jurisdiction of the courts in that location.</p>
        </Content>
      </Section>
      
      <LastUpdated>Last Updated: February 21, 2025</LastUpdated>
    </PageContainer>
  );
};

export default TermsPage; 